"use client";

import {createContext, useContext, useEffect, useState, type ReactNode} from 'react';
import Link from 'next/link';
import {usePathname} from 'next/navigation';
import {Home, Library, Sparkles, UserRound, PanelLeftClose, PanelLeftOpen, Plus} from 'lucide-react';
import {api} from '@/lib/api';
import type {AuthState} from '@/types/analysis';
import {BiteBrand} from './bite-brand';
import {BiteButton,BiteLinkButton} from './bite-ui';
import {BiteLoginRequired} from './bite-login-required';
import './bite-app-shell.css';
import type {BiteOwnedCardData} from './bite-card-pack';
import {BiteCompanyLogo} from './bite-company-logo';
import {koreanCompanyName} from '@/lib/korean-company-name';
import {biteSidebarSymbols,biteSidebarChange,biteSidebarPercent,type BiteSidebarQuote} from '@/lib/bite-sidebar-quotes';
import {trackBiteEvent} from '@/lib/bite-measurement';
import {BiteCreditWallet} from './bite-credit-wallet';

export type BiteAppAccess = {monthlyActive:boolean; bundleActive?:boolean; complimentaryActive?:boolean};
export const biteCanSubscribe = (admin:boolean, access:BiteAppAccess) =>
  !admin && !access.monthlyActive && !access.bundleActive && !access.complimentaryActive;
const appPaths = ['/bite/home', '/bite/collection', '/bite/assets', '/bite/pro', '/bite/me'];
export const biteUsesAppShell = (pathname:string) => pathname.startsWith('/bite/') && !['/bite/login','/bite/membership'].includes(pathname);
export const biteAllowsGuestContent = (pathname:string) => !appPaths.includes(pathname) || pathname==='/bite/home';
export const biteIsReportPath = (pathname:string) =>
  /^\/bite\/(card|shared|examples|preview)\/[^/]+\/?$/.test(pathname) ||
  (biteUsesAppShell(pathname) && ![...appPaths,'/bite/quote-data'].includes(pathname) && /^\/bite\/[^/]+$/.test(pathname));
export function biteRecentReportLinks(cards:BiteOwnedCardData[]) {
  const seen=new Set<string>();
  return [...cards].sort((a,b)=>b.openedAt.localeCompare(a.openedAt)).filter(card=>{
    if(seen.has(card.ticker))return false;
    seen.add(card.ticker);return true;
  }).map(card=>({id:card.jobId,ticker:card.ticker,name:koreanCompanyName(card.ticker,card.companyName)||card.ticker,href:`/bite/card/${encodeURIComponent(card.jobId)}`}));
}
type Session = {auth:Extract<AuthState,{loggedIn:true}>; access:BiteAppAccess|null; refresh:()=>void};
const SessionContext = createContext<Session|null>(null);
export const useBiteAppSession = () => useContext(SessionContext);

export function BiteAppFrame({children, pathname, showPro, name='내 계정', picture, recentCards=[]}: {
  children:ReactNode; pathname:string; showPro:boolean; name?:string; picture?:string; recentCards?:BiteOwnedCardData[];
}) {
  const [preferredCollapsed,setPreferredCollapsed]=useState(false);
  const [sidebarOverride,setSidebarOverride]=useState<{pathname:string;collapsed:boolean}|null>(null);
  const reportPath=biteIsReportPath(pathname);
  const collapsed=sidebarOverride?.pathname===pathname?sidebarOverride.collapsed:reportPath||preferredCollapsed;
  // Each newly opened report starts compact, without overwriting the home preference.
  if(sidebarOverride&&sidebarOverride.pathname!==pathname)setSidebarOverride(null);
  useEffect(()=>{try{setPreferredCollapsed(localStorage.getItem('bite_sidebar_collapsed')==='1');}catch{}},[]);
  const toggle=()=>{
    setSidebarOverride({pathname,collapsed:!collapsed});
    if(!reportPath){setPreferredCollapsed(!collapsed);try{localStorage.setItem('bite_sidebar_collapsed',collapsed?'0':'1');}catch{}}
    try{trackBiteEvent('click',{label:collapsed?'bite.sidebar.open':'bite.sidebar.close',placement:'app_sidebar'});}catch{}
  };
  const links = [{href:'/bite/home', label:'홈', Icon:Home}, {href:'/bite/collection',label:'내 보관함',Icon:Library},
    {href:'/bite/assets',label:'내 투자',Icon:Sparkles},...(showPro ? [{href:'/bite/pro',label:'Pro 구독하기',Icon:Sparkles}] : []), {href:'/bite/me',label:'내 계정',Icon:UserRound}];
  const recent=biteRecentReportLinks(recentCards);
  const symbolsKey=biteSidebarSymbols(recent.map(item=>item.ticker)).join(',');
  const [quotes,setQuotes]=useState<BiteSidebarQuote[]>([]);
  const [quoteFailed,setQuoteFailed]=useState(false);
  useEffect(()=>{
    if(!symbolsKey||collapsed)return;
    const controller=new AbortController();let timer:ReturnType<typeof setTimeout>;
    const symbols=symbolsKey.split(',');
    async function load(){
      if(!document.hidden&&window.matchMedia('(min-width:768px)').matches){
        let failed=false;
        // Same 25-symbol batches and extended-session priority as Bullstory's watchlist.
        for(let i=0;i<symbols.length&&!controller.signal.aborted;i+=25){
          const batch=symbols.slice(i,i+25);
          try{const result=await api<{quotes:BiteSidebarQuote[]}>(`/bite/quote-data?symbols=${encodeURIComponent(batch.join(','))}`,{signal:controller.signal});
            if(!controller.signal.aborted)setQuotes(previous=>[...previous.filter(q=>!batch.includes(q.symbol)),...result.quotes]);
          }catch{failed=true;if(!controller.signal.aborted)setQuotes(previous=>previous.filter(q=>!batch.includes(q.symbol)));}
        }
        if(!controller.signal.aborted)setQuoteFailed(failed);
      }
      if(!controller.signal.aborted)timer=setTimeout(load,15000);
    }
    void load();return()=>{controller.abort();clearTimeout(timer);};
  },[symbolsKey,collapsed]);
  const renderLink=({href,label,Icon}:typeof links[number])=><Link key={href} href={href} data-track={`bite.navigation.${href.split('/').pop()}`} aria-label={label} aria-current={pathname===href?'page':undefined} data-primary={href==='/bite/home'||href==='/bite/collection'||undefined} data-account={href==='/bite/me'||undefined}><Icon aria-hidden="true"/><span className={href==='/bite/pro'?'bite-app-nav__desktop-label':undefined}>{label}</span>{href==='/bite/pro'&&<span className="bite-app-nav__mobile-label" aria-hidden="true">Pro</span>}</Link>;
  return <div className="bite-app bite-scope bite-light" data-system="bite" data-bite-theme="light" data-collapsed={collapsed} data-report={reportPath} data-workspace={pathname==='/bite/assets'||undefined}>
    <a className="bite-app-skip" href="#bite-app-main">본문으로 이동</a>
    <aside className="bite-app-sidebar">
      <div className="bite-app-brand"><BiteBrand href="/bite/home"/><button className="bite-app-collapse" data-no-track onClick={toggle} aria-controls="bite-app-navigation" aria-label={collapsed?'사이드바 열기':'사이드바 닫기'} aria-expanded={!collapsed}>{collapsed?<PanelLeftOpen aria-hidden="true"/>:<PanelLeftClose aria-hidden="true"/>}</button></div>
      <nav className="bite-app-nav" id="bite-app-navigation" aria-label="Bite 메뉴">
        {[links[2],...links.slice(0,2)].map(renderLink)}
        {recent.length>0&&<section className="bite-app-recent" aria-label="최근 분석한 종목"><h2>최근 분석한 종목</h2><Link href="/bite/home#bite-search" className="bite-app-recent__new"><Plus aria-hidden="true"/><span>새 종목 분석하기</span></Link><div>{recent.map(item=>{const change=biteSidebarChange(quotes,item.ticker);return <Link key={item.id} href={item.href} aria-current={pathname===item.href?'page':undefined} title={item.name}><BiteCompanyLogo ticker={item.ticker} companyName={item.name}/><span className="bite-app-recent__name">{item.name}</span><span className="bite-app-recent__quote"><strong data-direction={change==null||change===0?'flat':change>0?'up':'down'}>{change==null&&quoteFailed?'조회 지연':biteSidebarPercent(change)}</strong><small>{item.ticker}</small></span></Link>;})}</div></section>}
        {links.slice(3).map(renderLink)}
      </nav>
      <div className="bite-app-sidebar-bottom">
        <BiteLinkButton href="https://bullstory.io" navigation="document" variant="secondary" className="bite-app-maker" data-track="bite.sidebar.bullstory"><img src="/logo-bull.png" alt=""/>불스토리</BiteLinkButton>
        <div className="bite-app-identity-row">
          <Link href="/bite/me" className="bite-app-profile" data-track="bite.sidebar.profile">{picture?<img src={picture} alt="" referrerPolicy="no-referrer"/>:<UserRound aria-hidden="true"/>}<span>{name}</span></Link>
          {name!=='로그인'&&<BiteCreditWallet compact/>}
        </div>
      </div>
    </aside>
    <div className="bite-app-content" id="bite-app-main" tabIndex={-1}>{children}</div>
  </div>;
}

function AuthenticatedApp({children,pathname}:{children:ReactNode;pathname:string}) {
  const [auth,setAuth]=useState<Extract<AuthState,{loggedIn:true}>|null>(null);
  const [guest,setGuest]=useState(false);
  const [access,setAccess]=useState<BiteAppAccess|null>(null);
  const [cards,setCards]=useState<BiteOwnedCardData[]>([]);
  const [error,setError]=useState('');
  const [attempt,setAttempt]=useState(0);
  useEffect(()=>{
    const controller=new AbortController();
    api<AuthState>('/api/me',{signal:controller.signal,cache:'no-store'}).then(result=>{
      if(controller.signal.aborted)return;
      if(result.loggedIn){setAuth(result);setGuest(false);setError('');}
      else setGuest(true);
    }).catch(()=>{if(!controller.signal.aborted)setError('계정을 불러오지 못했습니다.');});
    return()=>controller.abort();
  },[attempt]);
  useEffect(()=>{
    if(!auth)return;
    const controller=new AbortController();
    api<BiteAppAccess&{cards:BiteOwnedCardData[]}>('/api/ticker/bite/cards',{signal:controller.signal,cache:'no-store'})
      .then(value=>{if(!controller.signal.aborted){setAccess(value);setCards(value.cards||[]);setError('');}})
      .catch(()=>{if(!controller.signal.aborted)setError('이용 플랜을 불러오지 못했습니다.');});
    return()=>controller.abort();
  },[auth,attempt,pathname]);
  const retry=()=>setAttempt(value=>value+1);
  return <SessionContext.Provider value={auth?{auth,access,refresh:retry}:null}><BiteAppFrame pathname={pathname} showPro={Boolean(auth&&access&&biteCanSubscribe(auth.isAdmin,access))} name={auth?.name||'로그인'} picture={auth?.picture} recentCards={auth?cards:[]}>
    {auth&&error&&<div className="bite-app-error" role="alert">{error}<BiteButton variant="ghost" onClick={retry}>다시 시도</BiteButton></div>}
    {auth||biteAllowsGuestContent(pathname)?children:guest?<BiteLoginRequired pathname={pathname}/>:<div className="bite-app-loading" role="status">{error?<><p>{error}</p><BiteButton onClick={retry}>다시 시도</BiteButton></>:'로그인 확인 중'}</div>}
  </BiteAppFrame></SessionContext.Provider>;
}

export function BiteAppShell({children}:{children:ReactNode}) {
  const pathname=usePathname();
  return biteUsesAppShell(pathname)?<AuthenticatedApp pathname={pathname}>{children}</AuthenticatedApp>:children;
}
